import posthog from 'posthog-js';
import { featureFlags } from '../featureFlags';

export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  timestamp?: number;
}

export interface UserProperties {
  email?: string;
  name?: string;
  organizationId?: string;
  organizationName?: string;
  plan?: string;
  role?: string;
}

class AnalyticsService {
  private isInitialized = false;
  private userId: string | null = null;
  private enabled = false;

  constructor() {
    // Check if PostHog should be enabled
    this.enabled = featureFlags.ENABLE_POSTHOG_ANALYTICS ?? false;
  }

  initialize(apiKey?: string, host?: string) {
    if (!this.enabled) {
      console.log('[Analytics] PostHog is disabled via feature flag');
      return;
    }

    if (this.isInitialized) {
      console.warn('[Analytics] PostHog already initialized');
      return;
    }

    // Use environment variables or provided values
    const posthogKey = apiKey || import.meta.env.VITE_POSTHOG_KEY || 'phc_test_key';
    const posthogHost = host || import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

    if (!posthogKey || posthogKey === 'phc_test_key') {
      console.warn('[Analytics] No valid PostHog API key provided, analytics disabled');
      this.enabled = false;
      return;
    }

    try {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: false,
        loaded: (ph) => {
          console.log('[Analytics] PostHog loaded successfully');
        },
      });

      this.isInitialized = true;
      console.log('[Analytics] PostHog initialized');
    } catch (error) {
      console.error('[Analytics] Failed to initialize PostHog:', error);
      this.enabled = false;
    }
  }

  identify(userId: string, properties?: UserProperties) {
    if (!this.enabled || !this.isInitialized) return;

    this.userId = userId;
    posthog.identify(userId, properties);
    console.log('[Analytics] User identified:', userId, properties);
  }

  reset() {
    if (!this.enabled || !this.isInitialized) return;

    this.userId = null;
    posthog.reset();
    console.log('[Analytics] User reset');
  }

  trackEvent(eventName: string, properties?: Record<string, any>) {
    if (!this.enabled || !this.isInitialized) return;

    posthog.capture(eventName, {
      ...properties,
      timestamp: Date.now(),
    });
    
    if (import.meta.env.DEV) {
      console.log('[Analytics] Event tracked:', eventName, properties);
    }
  }

  trackPageView(pageName?: string, properties?: Record<string, any>) {
    if (!this.enabled || !this.isInitialized) return;

    posthog.capture('$pageview', {
      $current_url: window.location.href,
      $host: window.location.host,
      $pathname: window.location.pathname,
      page_name: pageName,
      ...properties,
    });
  }

  setUserProperty(key: string, value: any) {
    if (!this.enabled || !this.isInitialized) return;

    posthog.people.set({ [key]: value });
  }

  // Mindmap-specific events
  trackMindmapEvent(action: string, mindmapId: string, additionalProps?: Record<string, any>) {
    this.trackEvent(`mindmap_${action}`, {
      mindmap_id: mindmapId,
      ...additionalProps,
    });
  }

  trackNodeEvent(action: string, nodeId: string, mindmapId: string, additionalProps?: Record<string, any>) {
    this.trackEvent(`node_${action}`, {
      node_id: nodeId,
      mindmap_id: mindmapId,
      ...additionalProps,
    });
  }

  trackCollaborationEvent(action: string, properties?: Record<string, any>) {
    this.trackEvent(`collaboration_${action}`, properties);
  }

  trackIntegrationEvent(integration: string, action: string, properties?: Record<string, any>) {
    this.trackEvent(`integration_${integration}_${action}`, properties);
  }

  // Feature usage tracking
  trackFeatureUsage(featureName: string, action: string = 'used', properties?: Record<string, any>) {
    this.trackEvent('feature_usage', {
      feature: featureName,
      action,
      ...properties,
    });
  }

  // Error tracking
  trackError(error: Error, context?: Record<string, any>) {
    if (!this.enabled || !this.isInitialized) return;

    posthog.capture('error', {
      error_message: error.message,
      error_stack: error.stack,
      error_name: error.name,
      ...context,
    });

    console.error('[Analytics] Error tracked:', error, context);
  }

  // Performance tracking
  trackPerformance(metric: string, value: number, unit: string = 'ms', context?: Record<string, any>) {
    this.trackEvent('performance_metric', {
      metric,
      value,
      unit,
      ...context,
    });
  }

  // Session recording control
  startSessionRecording() {
    if (!this.enabled || !this.isInitialized) return;
    posthog.startSessionRecording();
  }

  stopSessionRecording() {
    if (!this.enabled || !this.isInitialized) return;
    posthog.stopSessionRecording();
  }

  // Feature flags from PostHog
  isFeatureEnabled(flagName: string): boolean {
    if (!this.enabled || !this.isInitialized) return false;
    return posthog.isFeatureEnabled(flagName) ?? false;
  }

  getFeatureFlag(flagName: string): any {
    if (!this.enabled || !this.isInitialized) return null;
    return posthog.getFeatureFlag(flagName);
  }

  // Get PostHog instance for advanced usage
  getPosthogInstance() {
    if (!this.enabled || !this.isInitialized) return null;
    return posthog;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Export types
export type { AnalyticsService };





